import React from 'react';
/* The live thinking matrix — see `STATUS_GLYPH`. `ActivityGlyph` reaches for the same file for the
   same reason: there is one working mark in this product and it is a component, not an SVG. */
import { DotmSquare11 } from '~/components/ui/dotm-square-11';
import { EditableText, PanelHeaderButton } from '~/design-system/components/v1_5/Layout';
import { DeckIcon } from '~/design-system/elements/DeckIcon/DeckIcon';
import { cn } from '~/lib/utils';
import { KIND_ICON } from './KindMenu';
import themes from '~/design-system/components/v1_5/Header/HeaderTheme.module.css';
import styles from './DirectoryRow.module.css';
import type { DirectoryItem, DirectoryItemDrag, DirectoryKind, HeaderTheme } from './theme';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';

export interface DirectoryRowProps {
  item: DirectoryItem;
  /** Decides the leading slot: a 34x19 render for Artifacts, a 30x30 tile for everything else. */
  kind: DirectoryKind;
  /** The open item. There is exactly one, and it is the artifact the preview is showing. */
  selected?: boolean;
  onClick?: () => void;
  /**
   * Omit and the row offers no `⋯` at all — which is what an in-flight row does, because there is
   * nothing yet to duplicate, download or delete.
   */
  onMoreClick?: () => void;
  menuOpen?: boolean;
  theme?: HeaderTheme;
  /** Stories and visual tests only: paints the hover state without a real pointer. */
  forceHover?: boolean;
  /**
   * The name is open for editing. Controlled by the panel, because exactly one row in the list may
   * be under the caret and a row cannot know about its siblings.
   */
  editing?: boolean;
  onEditingChange?: (editing: boolean) => void;
  /**
   * Fires only for a name that actually changed. Omit it and the row never opens for editing at
   * all — a websites row has a hostname, which is not a name anybody chose.
   */
  onRename?: (next: string) => void;
  /**
   * Drag-to-reorder wiring from `SortableDirectoryItem`. Omit it and the row is exactly what it was
   * before reordering existed — which is how its own story file renders it.
   */
  drag?: DirectoryItemDrag;
  /**
   * This is the lifted copy in the drag overlay rather than a row in the list: it draws the press
   * state and takes no drag wiring of its own, because it is already following the pointer.
   */
  overlay?: boolean;
  className?: string;
}

/**
 * The in-flight glyphs, and the working two are a COMPONENT rather than a registry name.
 *
 * Guri, 2026-08-30, twice. First the circle-refresh spinner went — *"don't use that circle refresh
 * icon anywhere"* — and this map pointed at `agent-dots.svg` instead. He rejected that too, pointing
 * at this exact row: *"this must be the ThinkingIndicator UI; everywhere we have that loader for
 * thinking should be that component."* The SVG is a still diamond that the row fades in and out as
 * one block; the real mark is `DotmSquare11`, whose dots ripple outward from the centre. The
 * directory and the chat now render the identical thing from the identical file, which is the only
 * arrangement in which they cannot drift.
 *
 * 15px, not the chat's 16: at DPR1 the 15px raster is cleaner, because 5 dots across an odd box puts
 * the centre dot on a pixel centre.
 */
type StatusGlyph =
  /** The live thinking matrix. Its own animation, its own reduced-motion path — see the render. */
  | { kind: 'matrix'; size: number }
  | { kind: 'icon'; icon: string; size: number };

const STATUS_GLYPH: Record<'generating' | 'updating' | 'queued', StatusGlyph> = {
  generating: { kind: 'matrix', size: 15 },
  updating: { kind: 'matrix', size: 15 },
  /* Waiting is not working, and the clock is the whole distinction: nothing is happening to this
     item yet, so nothing about its mark should move. */
  queued: { kind: 'icon', icon: 'clock', size: 14 },
};

/**
 * The row's own box: a button, until a caret goes into the name.
 *
 * `forwardRef` because dnd-kit's activator ref lands here — on the row rather than the cell, so the
 * `⋯` beside it opens its menu instead of lifting the item.
 */
const Row = React.forwardRef<
  HTMLElement,
  { editing: boolean; children: React.ReactNode } & React.HTMLAttributes<HTMLElement>
>(function Row({ editing, children, ...rest }, ref) {
  return editing ? (
    <div ref={ref as React.Ref<HTMLDivElement>} {...rest}>
      {children}
    </div>
  ) : (
    <button type="button" ref={ref as React.Ref<HTMLButtonElement>} {...rest}>
      {children}
    </button>
  );
});

/**
 * One line of the Directory: a 46px row of leading mark, two lines of text, and whatever the row's
 * state puts on its right end.
 *
 * While it is generating the row grows a second storey — a 3px progress bar under it, inside a
 * shared wash — and the whole cell becomes 56px. It keeps its place in the list rather than being
 * pinned anywhere: the caption is explicit that an artifact "takes its place in the list while it
 * is being written, not after", so the list closes back up around it and nothing jumps when it
 * lands.
 */
export function DirectoryRow({
  item,
  kind,
  selected = false,
  onClick,
  onMoreClick,
  menuOpen = false,
  theme: themeProp,
  forceHover = false,
  editing = false,
  onEditingChange,
  onRename,
  drag,
  overlay = false,
  className,
}: DirectoryRowProps) {
  const theme = useV15Theme(themeProp);
  // In flight is not renameable: the name is still being written by the thing that is writing the
  // item, and a caret in it would be arguing with the stream.
  const renameable = onRename !== undefined && (item.status ?? 'ready') === 'ready';
  const status = item.status ?? 'ready';
  // A child row: indented under its parent, 32px instead of 46, and one line instead of two.
  const nested = item.nested === true;
  const generating = status === 'generating';
  const placeholder = status === 'generating' || status === 'queued';
  const glyph = status === 'ready' ? null : STATUS_GLYPH[status];
  /*
   * The `⋯` is drawn whatever the status.
   *
   * It was gated on `ready`, which read as "an item still arriving has nothing to act on" — but the
   * one action it always has is DELETE, and a row that is stuck is exactly the row you most want to
   * remove. A blank website card (`status: 'empty'` → `queued`) had no menu at all, so there was no
   * way to get rid of one from the directory.
   *
   * WHICH rows appear is still per item — `menuFor` withholds copy-url from a card with no address,
   * the extractors from an unfetched page — and `renameable` above keeps its own gate, because a
   * name being written by a stream is not a name to argue with.
   */
  const hasMenu = onMoreClick !== undefined;

  return (
    <div
      ref={drag?.ref}
      className={cn(
        themes[theme],
        styles.cell,
        nested && styles.nested,
        nested && item.swatch && styles.variant,
        selected && styles.selected,
        generating && styles.generating,
        forceHover && styles.forceHover,
        drag?.dragging && styles.dragging,
        overlay && styles.overlay,
        className,
      )}
      // The live transform, and the transition only while the list is shuffling. Runtime values, so
      // inline is the one place they can go.
      style={drag?.style}
      // The panel finds this cell to place the row menu against it. An attribute rather than a ref
      // handed upward, so the menu lands in the same place whether the pointer opened it or a
      // controlled caller did.
      data-directory-row={item.id}
    >
      {/*
        * A `<button>` at rest and a plain `<div>` while the name is being edited.
        *
        * Not a preference: an `<input>` inside a `<button>` is invalid markup, and the button eats
        * the clicks that should be placing a caret between two letters. Swapping the element is
        * what `TitleBlock` does for the deck name one bar up, for the same reason.
        *
        * The drag wiring goes with it. A press inside the field must not lift the row at 8px, and
        * `handleProps` is where that press would be heard.
        */}
      <Row
        editing={editing}
        ref={editing ? undefined : drag?.handleRef}
        {...(editing ? {} : drag?.handleProps)}
        className={cn(styles.row, hasMenu && styles.rowWithMenu)}
        // Not `aria-selected`: that belongs to a listbox option, and these are buttons in a plain
        // list. `aria-current` is the honest word for "this is the one you are looking at".
        aria-current={selected ? 'true' : undefined}
        onClick={editing ? undefined : onClick}
        // Two clicks land before this does, so the row selects itself twice on the way in. Selecting
        // the same id twice is the same state, which is the only reason this gesture can sit on top
        // of a click-to-select row at all.
        onDoubleClick={renameable ? () => onEditingChange?.(true) : undefined}
      >
        {kind === 'artifacts' ? (
          /* The artifact's own render, not an icon. The white rectangle Paper draws is a stand-in
             for a render that has not been passed in — and painting a literal white here would be
             painting a lie on a light theme, where a slide's white ground has to come from the
             slide. With no render the chip is the same neutral wash the placeholder uses, and the
             hairline is what separates the two: a ready chip has an edge, an in-flight one does
             not, which is the one distinction the design actually draws. */
          <span
            className={styles.thumb}
            data-placeholder={placeholder}
            style={
              item.thumbnail && !placeholder
                ? ({ '--hv15-dir-thumb': item.thumbnail } as React.CSSProperties)
                : undefined
            }
            aria-hidden
          />
        ) : (
          <span className={styles.tile} data-placeholder={placeholder} aria-hidden>
            {item.swatch ? (
              /* The ground itself, not a glyph: a variant row is a colour decision and the disc is the
                 only honest mark for it. */
              <span className={styles.swatch} style={{ background: item.swatch }} />
            ) : (
              /* A real favicon keeps its own colours; a kind mark takes the row's. */
              <DeckIcon
                name={item.icon ?? KIND_ICON[kind]}
                size={nested ? 14 : 16}
                preserveColors={item.iconBrand}
              />
            )}
          </span>
        )}

        <span className={styles.text} data-queued={status === 'queued'}>
          <EditableText
            className={styles.title}
            value={item.title}
            editing={editing}
            onEditingChange={(next) => onEditingChange?.(next)}
            onCommit={(next) => onRename?.(next)}
            label={`Rename ${item.title}`}
          />
          {nested ? null : <span className={styles.meta}>{item.meta}</span>}
        </span>

        {/* Live or not, in the row rather than behind a click. It sits before the `⋯`'s reserved
            slot, so it holds the same position whether or not the pointer is on the row. */}
        {item.site ? <span className={styles.dot} data-site={item.site} aria-hidden /> : null}

        {glyph ? (
          /* `data-working` carries the TONE only — subtle for work in progress, hint for a queue.
             It used to carry a 1600ms opacity shimmer as well, and that is gone: the matrix below
             animates itself, and a whole-glyph fade over a per-dot ripple is two animations
             describing one fact. Reduced motion is likewise the component's own business now
             (`usePrefersReducedMotion` inside it), so this file no longer has an opinion. */
          <span className={styles.status} data-working={glyph.kind === 'matrix'} aria-hidden>
            {glyph.kind === 'matrix' ? (
              <DotmSquare11 size={glyph.size} dotSize={2} />
            ) : (
              <DeckIcon name={glyph.icon} size={glyph.size} />
            )}
          </span>
        ) : null}
      </Row>

      {hasMenu ? (
        /* A sibling of the row, not a child of it: a button inside a button is invalid markup and
           swallows the outer click. It overlays the 24px the row reserves for it, so revealing it
           costs no reflow — the title is truncated to the same width whether or not you are
           pointing at the row. Paper lets the text column grow into that space at rest, but a
           title that re-truncates the instant the pointer arrives is text moving while you read
           it, which is a worse trade than eleven pixels. */
        <PanelHeaderButton
          icon="more"
          activeIcon="more-fill"
          active={menuOpen}
          // The row already paints a wash under the pointer; a chip inside it would be a wash on a
          // wash. `tone` is the lift alone, which is what the design draws here.
          hover="tone"
          label={`More actions for ${item.title}`}
          // Announces the menu rather than a pressed state, exactly as the header's `+` does.
          ariaHasPopup="menu"
          ariaExpanded={menuOpen}
          theme={theme}
          className={cn(styles.more, menuOpen && styles.moreOpen)}
          onClick={onMoreClick}
        />
      ) : null}

      {generating ? (
        <div className={styles.track} aria-hidden>
          <div
            className={styles.fill}
            style={
              { '--hv15-dir-progress': (item.progress ?? 0) / 100 } as React.CSSProperties
            }
          />
        </div>
      ) : null}
    </div>
  );
}

/**
 * A row with nothing in it yet.
 *
 * The shape of a row rather than a spinner, so you can already see how many are coming — the
 * caption's whole argument. Paper authors the two text bars at zero height, which leaves only the
 * thumbnail chip and makes the skeleton the shape of a thumbnail, not of a row; the bars are drawn
 * here because the caption asks for the row.
 *
 * It shares `DirectoryRow`'s stylesheet because it IS that row with the content taken out — the
 * 46px, the 8px, the 10px gap and the leading box are the same numbers, and a second file is how
 * they stop being.
 */
export function SkeletonRow({ theme: themeProp, className }: { theme?: HeaderTheme; className?: string }) {
  const theme = useV15Theme(themeProp);
  return (
    <div className={cn(themes[theme], styles.cell, className)} aria-hidden>
      <div className={styles.row}>
        <span className={styles.thumb} data-placeholder="true" />
        <span className={styles.text}>
          <span className={styles.bar} data-line="title" />
          <span className={styles.bar} data-line="meta" />
        </span>
      </div>
    </div>
  );
}
